import { jsx, jsxs } from "react/jsx-runtime";
import { ContentType } from "../../../enums/contentStack.js";
import { getConfigurationSettingByDeveloperKey } from "../../../services/content/business.lib.js";
import { hexToRgba } from "../../../utils/color.js";
import { ModuleSettingsStore } from "../../../utils/contentstack/moduleSettingsStore.js";
import { isVisibleOnWeb } from "../../../utils/contentstack/webApplicability.js";
import { Carousel } from "@nxt-costco-com/forge-components";
import { SpaceXs, SpaceLg, ColorWhite, SpaceMd } from "@nxt-costco-com/forge-design-tokens";
import { Analytics } from "../../Analytics/index.js";
import { AdBuilder } from "../AdBuilder/index.js";
import { AdBuilderWrapper } from "../AdSet/AdBuilderWrapper/index.js";
import { AdSetTitle } from "../AdSet/AdSetTitle.js";
import { HalfBannerWithFourCorners } from "./HalfBannerWithFourCorners/index.js";
import { processAdSetCostcoAnchorLinks, getAdSetCarouselContainerSx, isHalfBannerWithFourCorners, getDataLayout } from "./lib.js";
import { getMultiAdStyles, staticMultiRowStyles } from "./serverStyles.js";
import { ProductTileCarouselClient } from "../AdSetThirdParty/ProductTileCarousel/ProductTileCarouselClient.js";
import { CategorySet } from "../CategorySet/index.js";
import { MultiRow } from "../CategoryStatic/MultiRow.js";
import { CategoryTile } from "../CategoryTile/index.js";
import { FeatureHighlightCard } from "../FeatureHighlightCard/index.js";
import { FeatureHighlightCardV2 } from "../FeatureHighlightCardv2/index.js";
import { FourCorners } from "../FourCorners/index.js";
import { FourCornersCategoryTile } from "../FourCornersCategoryTile/index.js";
import { fourCornersCateogryTileStyles } from "../FourCornersCategoryTile/tile.styles.js";
import { CostcoBox } from "../../Util/CostcoBox.js";
import { ITEM_TYPE_CATEGORY, AD_SET_STYLE_ROTATING, AD_SET_STYLE_STATIC, ITEM_TYPE_PRODUCT, AD_CURATION_COSTCO, ONE_FOURTH_MIX_AD_TYPE } from "./constants.js";
import { AdSetContainer, HighlightCardGrid } from "./styles.js";
async function AdSetCostco({
  addToIndex,
  entry,
  locale,
  titleProps,
  sx = {},
  enableLivePreview,
  maxSlidesBySize,
  itemCuration
}) {
  var _a, _b, _c, _d, _e, _f, _g, _h, _i, _j, _k, _l, _m;
  if (!entry) {
    return null;
  }
  if (!isVisibleOnWeb(entry)) {
    return null;
  }
  const title = (_a = entry == null ? void 0 : entry.ad_set_heading_and_costco_url) == null ? void 0 : _a.title;
  const titleId = `${entry.uid}-${entry.title.toLowerCase().replace(/ /g, "_")}-title`;
  if (addToIndex && title) {
    processAdSetCostcoAnchorLinks({
      groupId: entry.uid,
      title,
      id: titleId
    });
  }
  const titlePropsWithId = {
    ...titleProps,
    titleId
  };
  let firstAdData = void 0;
  const allAdTypes = [];
  function setAdData(entry2) {
    allAdTypes.push(entry2.ad_type);
    if (!firstAdData) {
      firstAdData = entry2;
    }
  }
  const ads = [];
  if (entry.item_type !== ITEM_TYPE_CATEGORY) {
    for (const advert of entry.ad_content) {
      if (advert.ad_builder_block && ((_c = (_b = advert.ad_builder_block) == null ? void 0 : _b.ad_builder_ref) == null ? void 0 : _c.length)) {
        try {
          const AdComponent2 = await AdBuilder({
            entryId: (_e = (_d = advert.ad_builder_block) == null ? void 0 : _d.ad_builder_ref[0]) == null ? void 0 : _e.uid,
            locale: entry.locale,
            setAdData,
            enableLivePreview,
            sx: (entry == null ? void 0 : entry.ad_set_style) === AD_SET_STYLE_ROTATING && entry.rotating_options.multi_item_carousel ? {
              height: "100%",
              paddingLeft: (entry == null ? void 0 : entry.enable_background_foreground_banner) ? void 0 : SpaceXs
            } : {}
            // TODO: Determine if this is wanted
          });
          ads.push(AdComponent2);
        } catch (e) {
          console.error(e);
        }
      } else if (advert.feature_highlight_card_block) {
        const element = await FeatureHighlightCard({
          entryId: advert.feature_highlight_card_block.feature_highlight_card_ref[0].uid,
          locale,
          enableLivePreview
        });
        ads.push(element);
      } else if (advert.feature_highlight_card_v2) {
        const element = await FeatureHighlightCardV2({
          entryId: advert.feature_highlight_card_v2.feature_highlight_card_v2_ref[0].uid,
          locale,
          enableLivePreview
        });
        ads.push(element);
      }
    }
  }
  const categorySetTitleAlignment = (_f = entry.category_content) == null ? void 0 : _f.category_set_title_alignment;
  const { hex: titleBackgroundHexColor } = ((_g = entry.ad_set_background) == null ? void 0 : _g.title_background_color) || {};
  const { title_background_transparency: titleBackgroundTransparency } = entry.ad_set_background || {};
  const { hex: bodyBackgroundHexColor } = ((_h = entry.ad_set_background) == null ? void 0 : _h.body_background_color) || {};
  const { body_background_transparency: bodyBackgroundTransparency } = entry.ad_set_background || {};
  const titleBackgroundTransparencyNumber = Number(
    titleBackgroundTransparency
  );
  const titleAlignment = categorySetTitleAlignment ?? void 0;
  const titleBackgroundOpacity = 1 - titleBackgroundTransparencyNumber;
  const titleBackgroundColor = titleBackgroundHexColor && hexToRgba(titleBackgroundHexColor, titleBackgroundOpacity);
  const bodyBackgroundTransparencyNumber = Number(bodyBackgroundTransparency);
  const bodyBackgroundOpacity = 1 - bodyBackgroundTransparencyNumber;
  const bodyBackgroundColor = bodyBackgroundHexColor && hexToRgba(bodyBackgroundHexColor, bodyBackgroundOpacity);
  const isEntryFourCorners = entry.item_type === ITEM_TYPE_CATEGORY && entry.ad_set_style === AD_SET_STYLE_STATIC && ((_i = entry.static_options) == null ? void 0 : _i.display_style) === "four_corner";
  if (entry.item_type === ITEM_TYPE_CATEGORY && entry.category_content && entry.category_content.category_items && entry.category_content.category_items.length) {
    const isStaticMultiRow = entry.item_type === ITEM_TYPE_CATEGORY && entry.ad_set_style === AD_SET_STYLE_STATIC && ((_j = entry.static_options) == null ? void 0 : _j.display_style) === "multi_row";
    for (const categoryItem of entry.category_content.category_items) {
      ads.push(
        await CategoryTile({
          ...isEntryFourCorners ? fourCornersCateogryTileStyles : {},
          ...isStaticMultiRow ? staticMultiRowStyles : {},
          background: bodyBackgroundColor !== void 0 || bodyBackgroundColor !== null,
          entryId: categoryItem.uid,
          foreground: bodyBackgroundColor !== void 0 || bodyBackgroundColor !== null,
          locale,
          enableLivePreview
        })
      );
    }
  }
  let heroCarouselData = { key_value: [] };
  try {
    heroCarouselData = await getConfigurationSettingByDeveloperKey(
      ContentType.ModuleSettings,
      "homepage_hero_carousel",
      locale
    );
  } catch {
    heroCarouselData = { key_value: [] };
  }
  const msStore = new ModuleSettingsStore({
    key_value: heroCarouselData == null ? void 0 : heroCarouselData.key_value
  });
  const multiAdStyles = getMultiAdStyles(firstAdData);
  let AdComponent = /* @__PURE__ */ jsx(CostcoBox, { sx: multiAdStyles, children: ads });
  if (entry && entry.ad_set_style === AD_SET_STYLE_ROTATING) {
    if (entry.item_type === ITEM_TYPE_PRODUCT && entry.product_display_style) {
      const productIds = entry.product_content.product_list.map(
        (product) => product.itemNumber.toString()
      );
      return /* @__PURE__ */ jsx(AdBuilderWrapper, { entry, locale, children: /* @__PURE__ */ jsx(
        Analytics,
        {
          analyticData: {
            adItem: {
              style: "carousel",
              title: (_k = entry == null ? void 0 : entry.ad_set_heading_and_costco_url) == null ? void 0 : _k.title,
              type: "product"
            },
            title: entry.title
          },
          children: /* @__PURE__ */ jsx(
            CostcoBox,
            {
              sx: {
                marginTop: 2,
                width: (entry == null ? void 0 : entry.enable_background_foreground_banner) ? "100%" : `calc(100% - ${SpaceLg})`
              },
              children: /* @__PURE__ */ jsx(
                ProductTileCarouselClient,
                {
                  inventoryAware: entry.product_display_style.inventory_aware_listings,
                  productTileContainerSx: {
                    backgroundColor: ColorWhite,
                    borderRadius: 2,
                    minHeight: "100%",
                    padding: 2
                  },
                  randomizeItems: entry.rotating_options.randomize_items,
                  showActionButton: entry.product_display_style.show_action_button,
                  showReviews: entry.product_display_style.show_reviews,
                  slideGapSizes: {
                    desktop: "16px",
                    mobile: "8px",
                    tablet: "12px"
                  },
                  variant: entry.product_display_style.product_card_display_style,
                  itemNumbers: productIds,
                  locale: entry.locale,
                  sx,
                  testId: "CostcoAdSet",
                  titleProps: titlePropsWithId,
                  maxSlidesBySize,
                  product_tile_data_source: entry.product_display_style.product_tile_data_source,
                  itemCuration: AD_CURATION_COSTCO
                }
              )
            }
          )
        }
      ) });
    }
    const keyValue = msStore.key_value;
    AdComponent = /* @__PURE__ */ jsx(Analytics, { analyticData: { adItem: { style: "carousel" } }, children: /* @__PURE__ */ jsx(
      CostcoBox,
      {
        sx: getAdSetCarouselContainerSx({
          hasBackground: entry.custom_background,
          hasTitle: !!titleProps.title
        }),
        children: /* @__PURE__ */ jsx(
          Carousel,
          {
            itemsPerSlide: entry.rotating_options.multi_item_carousel ? { desktop: 5, mobile: 2, tablet: 3 } : 1,
            contentTypeJoinder: keyValue.contenttypejoinder,
            contentTypeLabel: keyValue.contenttypelabel,
            isAutoplay: entry.rotating_options.enable_autoplay,
            items: ads,
            label: keyValue.label,
            nextLabel: keyValue.nextlabel,
            pauseLabel: keyValue.pauselabel,
            playLabel: keyValue.playlabel,
            previousLabel: keyValue.previouslabel,
            randomizeItems: entry.rotating_options.randomize_items,
            slideGap: (entry == null ? void 0 : entry.enable_background_foreground_banner) ? SpaceMd : SpaceXs
          }
        )
      }
    ) });
  }
  if (firstAdData && firstAdData.ad_type === ONE_FOURTH_MIX_AD_TYPE) {
    AdComponent = /* @__PURE__ */ jsx(Analytics, { analyticData: { adItem: { style: "banner" } }, children: /* @__PURE__ */ jsx(FourCorners, { data: ads, type: entry.item_type }) });
  }
  if (isHalfBannerWithFourCorners(allAdTypes)) {
    AdComponent = /* @__PURE__ */ jsx(
      HalfBannerWithFourCorners,
      {
        ads,
        allAdTypes,
        itemType: entry.item_type
      }
    );
  }
  if (entry.item_type === ITEM_TYPE_CATEGORY) {
    if (entry.ad_set_style === AD_SET_STYLE_ROTATING) {
      const filteredAds = ads.filter((item) => item !== null);
      return /* @__PURE__ */ jsx(AdBuilderWrapper, { entry, locale, children: /* @__PURE__ */ jsx(Analytics, { analyticData: { adItem: { style: "carousel" } }, children: /* @__PURE__ */ jsxs(
        AdSetContainer,
        {
          sx: {
            ...sx,
            marginTop: !titlePropsWithId.title && SpaceMd,
            width: "100%"
          },
          "data-layout": firstAdData ? firstAdData == null ? void 0 : firstAdData.ad_type : void 0,
          ignoreBackground: true,
          children: [
            /* @__PURE__ */ jsx(
              AdSetTitle,
              {
                ...titlePropsWithId,
                isAdSetCostco: true
              }
            ),
            /* @__PURE__ */ jsx(
              CategorySet,
              {
                enableAutoplay: entry.rotating_options.enable_autoplay,
                enableTwoRowDisplay: entry.rotating_options.enable_two_row_display,
                randomizeItems: entry.rotating_options.randomize_items,
                ads: filteredAds,
                titleId,
                bodyBackgroundColor
              }
            )
          ]
        }
      ) }) });
    } else if (entry.ad_set_style === AD_SET_STYLE_STATIC) {
      const { static_options } = entry;
      const { maximum_tiles_per_row } = static_options || {};
      if (isEntryFourCorners) {
        return /* @__PURE__ */ jsx(AdBuilderWrapper, { entry, locale, children: /* @__PURE__ */ jsx(
          Analytics,
          {
            analyticData: { adItem: { style: "banner" } },
            children: /* @__PURE__ */ jsx(
              FourCornersCategoryTile,
              {
                titleProps: {
                  ...titlePropsWithId,
                  titleAlignment,
                  titleBackgroundColor: !entry.custom_background ? void 0 : titleBackgroundColor
                },
                bodyBackgroundColor,
                categoryTiles: ads,
                ignoreBackground: !entry.custom_background
              }
            )
          }
        ) });
      } else {
        const getMarginTop = () => {
          if (!titlePropsWithId.title) {
            return SpaceMd;
          } else if (entry == null ? void 0 : entry.enable_background_foreground_banner) {
            return SpaceXs;
          } else {
            return sx == null ? void 0 : sx.marginTop;
          }
        };
        const marginTop = getMarginTop();
        return /* @__PURE__ */ jsx(AdBuilderWrapper, { entry, locale, children: /* @__PURE__ */ jsx(
          Analytics,
          {
            analyticData: { adItem: { style: "banner" } },
            children: /* @__PURE__ */ jsxs(
              AdSetContainer,
              {
                sx: {
                  ...sx,
                  marginTop
                },
                bodyBackgroundColor,
                "data-layout": getDataLayout(allAdTypes),
                ignoreBackground: !entry.custom_background,
                children: [
                  titlePropsWithId && /* @__PURE__ */ jsx(
                    AdSetTitle,
                    {
                      ...titlePropsWithId,
                      isAdSetCostco: true,
                      titleAlignment,
                      titleBackgroundColor
                    }
                  ),
                  /* @__PURE__ */ jsx(
                    MultiRow,
                    {
                      ads,
                      maxTilesPerRow: maximum_tiles_per_row
                    }
                  )
                ]
              }
            )
          }
        ) });
      }
    }
  }
  if (entry.ad_content && (((_l = entry.ad_content[0]) == null ? void 0 : _l.feature_highlight_card_block) || ((_m = entry.ad_content[0]) == null ? void 0 : _m.feature_highlight_card_v2))) {
    AdComponent = /* @__PURE__ */ jsx(HighlightCardGrid, { children: ads });
  }
  return /* @__PURE__ */ jsx(AdBuilderWrapper, { entry, locale, children: /* @__PURE__ */ jsx(Analytics, { analyticData: { adItem: { style: "banner" } }, children: /* @__PURE__ */ jsxs(
    AdSetContainer,
    {
      sx: {
        ...sx,
        marginTop: (entry == null ? void 0 : entry.enable_background_foreground_banner) ? 2 : sx == null ? void 0 : sx.marginTop
      },
      bodyBackgroundColor,
      "data-layout": getDataLayout(allAdTypes),
      ignoreBackground: !entry.custom_background,
      children: [
        titlePropsWithId && /* @__PURE__ */ jsx(
          AdSetTitle,
          {
            ...titlePropsWithId,
            isAdSetCostco: true,
            titleAlignment,
            titleBackgroundColor
          }
        ),
        AdComponent
      ]
    }
  ) }) });
}
export {
  AdSetCostco
};
